import type ExcelJs from 'exceljs';

/**
 * Generates an array of Excel-style column names (e.g., A, B, C, ..., Z, AA, AB, ..., YZ)
 * up to the specified number of columns.
 *
 * @param {number} numColumns - The number of columns to generate.
 * @returns {string[]} An array of Excel column names.
 *
 * @example
 * // Generate column names for the first 30 columns
 * generateExcelColumnNames(30);
 * // Output: ["A", "B", "C", ..., "AD"]
 */
export const generateExcelColumnNames = (numColumns: number): string[] => {
  const getColumnName = (columnIndex: number) => {
    let index = columnIndex;
    let columnName = '';
    while (index >= 0) {
      // base-26 number system, where 'A' corresponds to 1, 'B' to 2, ..., 'Z' to 26.
      // Converts the index into the corresponding ASCII character (starting from 'A', which is 65 in ASCII)
      columnName = String.fromCharCode((index % 26) + 65) + columnName;
      index = Math.floor(index / 26) - 1;
    }
    return columnName;
  };

  return Array.from({ length: numColumns }, (_, i) => getColumnName(i));
};

/**
 * Creates and downloads an Excel file (.xlsx) with the specified content.
 *
 * @param {string} fileName - The name of the file (without extension) to be saved.
 * @param {ExcelJs.Buffer} content - The content of the file to be written and downloaded.
 *
 * @description
 * It creates an anchor (`<a>`) element
 * and triggers a download by simulating a click on it.
 */
export const writeFileAndDownload = (fileName: string, content: ExcelJs.Buffer) => {
  const link = document.createElement('a');
  const blob = new Blob([content], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  link.download = `${fileName}.xlsx`;
  link.href = URL.createObjectURL(blob);
  link.click();
};
